import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import './HeaderSearch.css';

// ── HEADER SEARCH ────────────────────────────────────────────────────────────
// Collapsed to an icon by default and expanding on click, so it costs one icon
// of header space until someone actually wants it.
//
// Submitting navigates to /search with the query in the URL rather than in
// component state — that makes a search result linkable, shareable and
// restorable on refresh, which state alone can't do.
//
// SIGNED-IN ONLY. A visitor without an account browses from the main page,
// which carries its own search and catalogue; the profile pages they can reach
// from there are a public read, not a place to start another query. Rendering
// nothing here rather than at each of the eight call sites, so the rule cannot
// be applied to seven of them and forgotten on the eighth.
export default function HeaderSearch() {
  const { t } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  // Collapse when the user clicks away, but only while empty — closing a field
  // someone has typed into would throw their query away.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target) && !q.trim()) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, q]);

  function submit(e) {
    e?.preventDefault();
    const term = q.trim();
    // An empty search is still a valid destination — it lands on the directory
    // with the filters available, which is exactly what the icon promises.
    navigate(term ? `/search?q=${encodeURIComponent(term)}` : '/search');
    setOpen(false);
  }

  // After every hook, never before one — a conditional hook order is the one
  // way this early return could break the component.
  if (!user) return null;

  return (
    <form
      ref={wrapRef}
      className={`hs${open ? ' hs--open' : ''}`}
      onSubmit={submit}
      role="search"
    >
      <input
        ref={inputRef}
        className="hs-input"
        type="search"
        value={q}
        onChange={e => setQ(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') { setQ(''); setOpen(false); } }}
        placeholder={t('search_ph')}
        aria-label={t('search_designers')}
        tabIndex={open ? 0 : -1}
      />
      <button
        type={open ? 'submit' : 'button'}
        className="hs-btn"
        onClick={() => { if (!open) setOpen(true); }}
        aria-label={t('search_designers')}
        title={t('search_designers')}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
      </button>
    </form>
  );
}
